import { Inject, Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { VERSION } from 'projects/osn-shared/version';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, takeUntil, tap } from 'rxjs/operators';
import { CONFIG_TOKEN } from '../../../../../../../config-token';
import { setCxbIsLoading } from '../../../app.actions';
import { RootAppState } from '../../../app.reducer';
import { Config } from '../../../config';
import { PageAccesses } from '../../../core/guards/page-accesses.model';
import { AuthService } from '../../../core/services/auth.service';
import { PageAccessesService } from '../../../core/services/page-access/page-access.service';
import { MenuBarViewModel } from './menu-bar.view-model';
import { AdminUiMenuItemType, AdminUiSubMenuItemType } from '../../../domain/admin-ui-details';
import { AdminUiDetailsService } from '../../../admin-ui-details/admin-ui-details.service';
import { selectApiVersion, selectCxbRootLevelRoles } from '../../../app.selectors';

const MenuPageItemUrl = {
  [AdminUiMenuItemType.Page]: '/pages',
  [AdminUiMenuItemType.ContentGroup]: '/content-groups',
  [AdminUiMenuItemType.MenuEditor]: '/menu-editor',
  [AdminUiMenuItemType.Settings]: '/settings',
};

@Injectable()
export class MenuBarService implements OnDestroy {
  public menuItems$: Observable<{ url: string; title: string }[]>;
  public settingsMenuItems$: Observable<{ url: string; title: string }[]>;
  public tenantIndependentMenuItems$: Observable<{ url: string; title: string }[]>;
  public readonly showVersion = this.config.showVersion;
  public readonly version = this.showVersion ? VERSION : undefined;
  public readonly apiVersion$ = this.store.pipe(select(selectApiVersion));
  private readonly onDestroy$ = new Subject<void>();

  public isLoading$ = this.authService.isLoading$.pipe(tap((loading) => this.store.dispatch(setCxbIsLoading({ loading }))));
  public username$ = this.authService.user$.pipe(map((user) => user?.username));
  public isLoggedIn$ = this.authService.user$.pipe(map((user) => !!user));
  public isNavigateToOnDemandContentGroupLinkAvailable$ = this.store
    .select(selectCxbRootLevelRoles)
    .pipe(
      map(
        (rootLevelRoles) =>
          !!rootLevelRoles?.length &&
          (rootLevelRoles.includes('OndemandContentGroupEditor') ||
            rootLevelRoles.includes('OndemandContentGroupViewer') ||
            rootLevelRoles.includes('OsntvStoreContentGroupEditor') ||
            rootLevelRoles.includes('OsntvStoreContentGroupEditor'))
      )
    );

  public vm$: Observable<MenuBarViewModel> = combineLatest([this.isLoading$, this.isLoggedIn$, this.username$]).pipe(
    takeUntil(this.onDestroy$),
    map(([isLoading, isLoggedIn, username]) => ({ isLoading, isLoggedIn, username }))
  );

  public accesses: PageAccesses;

  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly store: Store<RootAppState>,
    @Inject(CONFIG_TOKEN) private readonly config: Config,
    private readonly pageAccessesService: PageAccessesService,
    private readonly adminUiDetailsService: AdminUiDetailsService
  ) {
    const menuBuilderObservables = {
      accesses: this.pageAccessesService.usersPageAccesses$,
      adminUIDetails: this.adminUiDetailsService.adminUIDetails$,
    };
    this.menuItems$ = combineLatest(menuBuilderObservables).pipe(
      takeUntil(this.onDestroy$),
      filter(({ accesses, adminUIDetails }) => {
        return !!accesses && !!adminUIDetails;
      }),
      map(({ accesses, adminUIDetails }) => {
        const menuItems = [];
        const pushMenu = (menuType: AdminUiMenuItemType, hasAccess: boolean) => {
          const menuItem = adminUIDetails?.menuItems.find((item) => item.type === menuType);
          if (menuItem && hasAccess) {
            menuItems.push({ url: menuItem.url ?? MenuPageItemUrl[menuType], title: menuItem.name });
          }
        };

        pushMenu(AdminUiMenuItemType.Page, accesses.menuItem.launcher);
        pushMenu(AdminUiMenuItemType.ContentGroup, accesses.menuItem.contentGroups);
        pushMenu(AdminUiMenuItemType.MenuEditor, accesses.menuItem.menuEditor);
        pushMenu(
          AdminUiMenuItemType.Settings,
          accesses.menuItem.applicationListAndMetadata ||
            accesses.menuItem.channelList ||
            accesses.menuItem.configEditor ||
            accesses.menuItem.brandSettings
        );
        pushMenu(AdminUiMenuItemType.RedirectPolicies, accesses.menuItem.redirectPolicies);

        return menuItems;
      })
    );

    this.settingsMenuItems$ = combineLatest(menuBuilderObservables).pipe(
      takeUntil(this.onDestroy$),
      map(({ accesses, adminUIDetails }) => {
        const settingsMenu = adminUIDetails?.menuItems.find((item) => item.type === AdminUiMenuItemType.Settings);
        return { accesses, settingsMenu };
      }),
      filter(({ accesses, settingsMenu }) => {
        return !!accesses && !!settingsMenu;
      }),
      map(({ accesses, settingsMenu }) => {
        const menuItems = [];
        const pushMenu = (subMenuType: AdminUiSubMenuItemType, hasAccess: boolean) => {
          const menuItem = settingsMenu?.subMenuItems?.find((item) => item.type === subMenuType);
          if (!!menuItem && hasAccess) {
            menuItems.push({ url: menuItem.url, title: menuItem.name });
          }
        };
        pushMenu(AdminUiSubMenuItemType.App, accesses.menuItem.applicationListAndMetadata);
        pushMenu(AdminUiSubMenuItemType.Brand, accesses.menuItem.brandSettings);
        pushMenu(AdminUiSubMenuItemType.Channel, accesses.menuItem.channelList);
        pushMenu(AdminUiSubMenuItemType.TenantConfig, accesses.menuItem.configEditor);

        return menuItems;
      })
    );

    this.tenantIndependentMenuItems$ = this.pageAccessesService.usersPageAccesses$.pipe(
      takeUntil(this.onDestroy$),
      filter((accesses) => {
        return accesses !== null;
      }),
      map((accesses) => {
        const menuItems = [];
        if (accesses.tenantIndependentMenuItem.generalStbConfigEditor) {
          menuItems.push({ url: '/root/general-stb-config-editor', title: 'General STB config editor' });
        }
        if (accesses.tenantIndependentMenuItem.generalStreamingConfigEditor) {
          menuItems.push({ url: '/root/general-streaming-config-editor', title: 'General streaming config editor' });
        }
        if (this.isNavigateToOnDemandContentGroupLinkAvailable$) {
          menuItems.push({ url: '/root/content-groups', title: 'Content groups' });
        }
        return menuItems;
      })
    );
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  public logout(): void {
    this.authService.logout();
  }

  public login(): void {
    // navigate to '/' will trigger a login redirect
    this.router.navigate(['']);
  }
}
